// TOUR-4c — animate firefly back to the dock tab when a tour ends or is canceled.

import { useCallback, useRef, useState } from "react";
import type { FireflyIntent } from "@/components/activity/firefly/fireflyIntents";
import {
  queryFireflyTargetById,
  readFireflyTargetRect,
} from "@/lib/firefly/fireflyTargetConvention";
import type { FireflyTourTravelPoint } from "@/components/activity/firefly/useFireflyTourTravel";

const RETURN_TRAVEL_INTENT = "guidance" as const;
const RETURN_ARRIVAL_INTENT = "discovery" as const;
const RETURN_ARRIVAL_DWELL_MS = 450;

export type FireflyTourReturnPhase = {
  active: true;
  intent: Extract<FireflyIntent, "teaching" | "discovery" | "guidance">;
};

export type UseFireflyTourReturnHomeOptions = {
  reducedMotion: boolean;
  flyTo: (from: FireflyTourTravelPoint, to: FireflyTourTravelPoint) => Promise<void>;
  cancelTravel: () => void;
  onFinalize: (reason: "cancel" | "complete") => void;
};

export function useFireflyTourReturnHome(options: UseFireflyTourReturnHomeOptions) {
  const { reducedMotion, flyTo, cancelTravel, onFinalize } = options;
  const [returnPhase, setReturnPhase] = useState<FireflyTourReturnPhase | null>(null);
  const pendingEndRef = useRef<"cancel" | "complete" | null>(null);

  const beginReturnHome = useCallback(
    async (reason: "cancel" | "complete", from: FireflyTourTravelPoint | null) => {
      if (pendingEndRef.current) return;
      pendingEndRef.current = reason;
      cancelTravel();

      const dockTab = queryFireflyTargetById("activity-dock-tab");
      const to = dockTab
        ? (() => {
            const rect = readFireflyTargetRect(dockTab);
            return { x: rect.centerX, y: rect.centerY };
          })()
        : null;

      if (!from || !to || reducedMotion) {
        setReturnPhase(null);
        pendingEndRef.current = null;
        onFinalize(reason);
        return;
      }

      setReturnPhase({ active: true, intent: RETURN_TRAVEL_INTENT });
      await flyTo(from, to);
      setReturnPhase({ active: true, intent: RETURN_ARRIVAL_INTENT });
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, RETURN_ARRIVAL_DWELL_MS);
      });
      setReturnPhase(null);
      pendingEndRef.current = null;
      onFinalize(reason);
    },
    [cancelTravel, flyTo, onFinalize, reducedMotion],
  );

  const resetReturnHome = useCallback(() => {
    pendingEndRef.current = null;
    setReturnPhase(null);
  }, []);

  return {
    returnPhase,
    isReturningHome: returnPhase?.active === true,
    returnTravelIntent: returnPhase?.intent ?? null,
    beginReturnHome,
    resetReturnHome,
    isEndPending: () => pendingEndRef.current != null,
  };
}
